import { useEffect, useRef, useState } from 'react';
import { useChatStore } from '@/state/chatStore';
import { chatTransport } from '@/lib/chatTransport';

/**
 * pre186: Berater-Chat-Drawer — rein 1:1 und Ende-zu-Ende (P2P-DataChannel).
 * Inhalte verlassen nie den Server; der Verlauf liegt nur im RAM (Reload/Logout löscht ihn).
 */
export function ChatDrawer() {
  const open = useChatStore((s) => s.open);
  const peerId = useChatStore((s) => s.peerId);
  const peerName = useChatStore((s) => s.peerName);
  const threads = useChatStore((s) => s.threads);
  const names = useChatStore((s) => s.names);
  const statusMap = useChatStore((s) => s.status);
  const unread = useChatStore((s) => s.unread);
  const openChat = useChatStore((s) => s.openChat);
  const showList = useChatStore((s) => s.showList);
  const close = useChatStore((s) => s.close);

  const [text, setText] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  const msgs = peerId ? (threads[peerId] ?? []) : [];
  const status = peerId ? (statusMap[peerId] ?? 'idle') : 'idle';

  // Aktiver Thread → Verbindung sicherstellen + als gelesen markieren.
  useEffect(() => {
    if (open && peerId) { void chatTransport.ensureConnection(peerId); chatTransport.markRead(peerId); }
  }, [open, peerId]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length, open, peerId]);

  const send = () => {
    const body = text.trim();
    if (!body || !peerId) return;
    chatTransport.send(peerId, peerName, body);
    setText('');
  };

  const fmt = (ts: number) => new Date(ts).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  const statusLabel = (st: string) =>
    st === 'open' ? 'Ende-zu-Ende verbunden'
      : st === 'connecting' ? 'Verbindung wird aufgebaut…'
        : st === 'closed' ? 'Verbindung getrennt'
          : '';

  if (!open) return null;

  // Liste: alle Peers mit Verlauf oder ungelesenen Nachrichten.
  const peerIds = Array.from(new Set([...Object.keys(threads), ...Object.keys(unread.scopes)]));

  return (
    <div style={panel}>
      <div style={head}>
        {peerId ? (
          <button onClick={showList} style={backBtn} title="Konversationen">‹</button>
        ) : <span style={{ width: 22 }} />}
        <span style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span style={{ fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {peerId ? (names[peerId] || peerName || 'Berater') : 'Chat'}
          </span>
          {peerId && <span style={{ fontSize: 10.5, color: status === 'open' ? 'var(--ok, #22C55E)' : 'var(--muted, #8aa)' }}>{statusLabel(status)}</span>}
        </span>
        <button onClick={close} style={closeBtn} title="Schließen">✕</button>
      </div>

      {!peerId ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {peerIds.length === 0 ? (
            <div style={{ color: 'var(--muted, #8aa)', fontSize: 13, padding: 16, lineHeight: 1.5 }}>
              Noch keine Konversationen.<br />Tippe links auf das Chat-Symbol neben einem Kollegen, um eine Ende-zu-Ende-Unterhaltung zu starten.
            </div>
          ) : peerIds.map((pid) => (
            <button key={pid} onClick={() => openChat(pid, names[pid] || 'Berater')} style={listRow}>
              <span style={{ flex: 1, textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{names[pid] || 'Berater'}</span>
              {unread.scopes[pid] > 0 && <span style={badge}>{unread.scopes[pid]}</span>}
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {msgs.length === 0 && <div style={{ color: 'var(--muted, #8aa)', fontSize: 12.5, textAlign: 'center', marginTop: 20 }}>Noch keine Nachrichten.</div>}
            {msgs.map((m) => (
              <div key={m.id} style={{ alignSelf: m.mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
                <div style={{ ...bubble, ...(m.mine ? bubbleMine : bubbleOther) }}>{m.body}</div>
                <div style={{ ...timeLbl, textAlign: m.mine ? 'right' : 'left' }}>{fmt(m.ts)}</div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <div style={inputRow}>
            <input
              value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Nachricht…" style={chatInput} autoFocus
            />
            <button onClick={send} disabled={!text.trim()} style={sendBtn}>Senden</button>
          </div>
        </>
      )}
    </div>
  );
}

const panel: React.CSSProperties = {
  position: 'fixed', top: 0, right: 0, bottom: 0, width: 340, maxWidth: '92vw', zIndex: 1400,
  display: 'flex', flexDirection: 'column', background: 'var(--bg-elev-1, #161616)',
  borderLeft: '1px solid var(--hairline, #232323)', boxShadow: '-8px 0 30px rgba(0,0,0,.35)',
};
const head: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', borderBottom: '1px solid var(--hairline, #232323)' };
const backBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--fg, #eee)', fontSize: 20, cursor: 'pointer', width: 22, lineHeight: 1, fontFamily: 'inherit' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--muted, #8aa)', fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' };
const listRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '11px 14px', background: 'transparent', border: 'none', borderBottom: '1px solid var(--hairline-soft, #1e1e1e)', color: 'var(--fg, #eee)', fontSize: 13.5, cursor: 'pointer', fontFamily: 'inherit' };
const badge: React.CSSProperties = { background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderRadius: 999, fontSize: 11, fontWeight: 700, padding: '1px 7px', minWidth: 18, textAlign: 'center' };
const bubble: React.CSSProperties = { padding: '7px 11px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' };
const bubbleMine: React.CSSProperties = { background: 'var(--tenant-accent, #7c3aed)', color: '#fff', borderBottomRightRadius: 4 };
const bubbleOther: React.CSSProperties = { background: 'var(--bg-elev-2, #232323)', color: 'var(--fg, #eee)', borderBottomLeftRadius: 4 };
const timeLbl: React.CSSProperties = { fontSize: 10, color: 'var(--muted, #8aa)', margin: '2px 4px 0' };
const inputRow: React.CSSProperties = { display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--hairline, #232323)' };
const chatInput: React.CSSProperties = { flex: 1, boxSizing: 'border-box', padding: '9px 11px', borderRadius: 9, border: '1px solid var(--hairline, #232323)', background: 'var(--bg-elev-2)', color: 'var(--fg, #eee)', fontSize: 13.5, fontFamily: 'inherit', outline: 'none' };
const sendBtn: React.CSSProperties = { border: 'none', borderRadius: 9, padding: '0 14px', background: 'var(--tenant-accent, #7c3aed)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' };
